import React from 'react';
import {
  Alert, Button, Grid, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import { PlanningCycleRow } from './planningCycle.models';

type Option = { rid?: number; id?: number; shc?: string; code?: string; nam?: string; name?: string };

type Props = {
  cycle?: PlanningCycleRow | null;
  phases: Option[];
  loading?: boolean;
  onActivate: (rid: number) => Promise<void>;
  onAdvance: (rid: number, phasecode: string, notes: string) => Promise<void>;
  onClose: (rid: number, notes: string) => Promise<void>;
};

type State = {
  nextphasecode: string;
  notes: string;
};

class PlanningCycleDetailPanel extends React.Component<Props, State> {
  state: State = { nextphasecode: '', notes: '' };

  codeOf = (x: Option) => String(x.shc ?? x.code ?? '');
  nameOf = (x: Option) => x.nam ?? x.name ?? x.shc ?? x.code ?? '';

  componentDidUpdate(prevProps: Props) {
    if (prevProps.cycle?.rid !== this.props.cycle?.rid) {
      this.setState({ nextphasecode: '', notes: '' });
    }
  }

  render() {
    const c = this.props.cycle;

    if (!c) {
      return (
        <Alert severity="info">
          Select a Planning Cycle to review its period, state, phase, and lifecycle actions.
        </Alert>
      );
    }

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{c.name}</Typography>
            <Typography variant="body2" color="text.secondary">{c.code}</Typography>
          </div>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">Budget Period</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {c.budgetperiodname || c.budgetperiodcode}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="caption" color="text.secondary">State</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {c.statename || c.statecode}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Phase</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {c.phasename || c.phasecode}
              </Typography>
            </Grid>
          </Grid>

          <Alert severity="info">
            Activating a cycle changes its lifecycle state. It is not a per-user selection flag.
          </Alert>

          <TextField
            select
            size="small"
            label="Next Phase"
            value={this.state.nextphasecode}
            onChange={(e) => this.setState({ nextphasecode: e.target.value })}
            fullWidth
          >
            {this.props.phases.map((x) => (
              <MenuItem key={this.codeOf(x)} value={this.codeOf(x)}>
                {this.nameOf(x)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Lifecycle Notes"
            value={this.state.notes}
            onChange={(e) => this.setState({ notes: e.target.value })}
            multiline
            minRows={3}
            fullWidth
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={() => this.props.onActivate(c.rid)}
              disabled={this.props.loading || c.statecode === 'ACTIVE'}
            >
              Activate
            </Button>

            <Button
              variant="contained"
              startIcon={<ArrowForwardIcon />}
              onClick={() =>
                this.props.onAdvance(c.rid, this.state.nextphasecode, this.state.notes)
              }
              disabled={this.props.loading || !this.state.nextphasecode}
            >
              Advance Phase
            </Button>

            <Button
              variant="outlined"
              color="warning"
              startIcon={<LockIcon />}
              onClick={() => this.props.onClose(c.rid, this.state.notes)}
              disabled={
                this.props.loading ||
                c.statecode === 'CLOSED' ||
                c.statecode === 'ARCHIVED'
              }
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}

export default PlanningCycleDetailPanel;
